// The browser's only way to reach data. Every call goes to the app backend,
// which holds the institutional credential and talks to the Pods. Nothing here
// knows about tokens, WebIDs-as-login, or Comunica — that all lives server-side.

import { role } from './role.svelte.js';

async function call(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      // The caller's role. The backend enforces what it permits; sending it
      // from here is the stand-in for an institutional SSO assertion.
      ...(role.current ? { 'X-Role': role.current } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({ error: `${res.status} ${res.statusText}` }));
  // A rejected config carries per-field reasons (server/index.js sends them as
  // `details`). Carry them onto the Error so callers can show which column is
  // wrong instead of "the request failed". A failed multi-write carries `step`
  // (which one broke) and `rollback` (what could not be put back) for the same
  // reason: the screen can only report a phase if the failure names one.
  if (!res.ok) {
    throw Object.assign(new Error(json.error ?? `${res.status} ${res.statusText}`),
      { status: res.status,
        ...(json.details ? { details: json.details } : {}),
        ...(json.step ? { step: json.step } : {}),
        ...(json.rollback ? { rollback: json.rollback } : {}) });
  }
  return json;
}

export const getContext = () => call('/context');
export const getHealth = () => call('/health');
// Pod-resident config: connector | mapping | aggregations | partners | queries |
// institution. Read live from the Pod each call — no build-time import, so a
// curl edit to the Pod shows up on the next fetch with no rebuild.
export const getConfig = (name) => call(`/config/${name}`);
export const getOverview = () => call('/overview');
export const getDashboard = () => call('/dashboard');
export const getDashboardPartners = () => call('/dashboard/partners');
export const rebuildDashboard = async ({ onProgress } = {}) => {
  const { id } = await call('/dashboard/rebuild', { method: 'POST' });
  return pollJob(id, onProgress);
};
export const getRecords = (options = {}) => {
  if (typeof options === 'number') options = { limit: options };
  const query = new URLSearchParams(Object.entries(options).filter(([, value]) => value !== null && value !== '' && value !== undefined));
  return call(`/records${query.size ? `?${query}` : ''}`);
};
export const getCatalog = () => call('/catalog');
// The institution's own private aggregate cubes, reconciled against what is
// declared and what is published. Nothing else in the app can see these.
export const getCubes = () => call('/cubes');
export const getSources = () => call('/sources');
export const getSource = (sourceId) => call(`/sources/${encodeURIComponent(sourceId)}`);
export const saveSource = (source) => call('/sources', { method: 'POST', body: source });
export const getDisclosure = (sourceId) => call(`/disclosure/${encodeURIComponent(sourceId)}`);
export const validateDisclosure = (draft) => call('/disclosure/validate', { method: 'POST', body: draft });
export const confirmDisclosure = (sourceId, draft) => call(`/disclosure/${encodeURIComponent(sourceId)}`, { method: 'POST', body: draft });
export const saveAggregate = (body) => call('/aggregates', { method: 'POST', body });
// What an aggregate would publish, counted over the private records in the Pod
// and written nowhere. Omit `aggregate` to summarise only what already exists.
export const previewAggregates = (body) => call('/aggregates/preview', { method: 'POST', body });
export const deleteAggregate = (sourceId, id) =>
  call(`/aggregates/${encodeURIComponent(id)}?sourceId=${encodeURIComponent(sourceId)}`, { method: 'DELETE' });
// The partner registry with live outbound/inbound probes, for the Partners screen.
export const getPartners = () => call('/partners');
export const getResource = (url) => call(`/resource?url=${encodeURIComponent(url)}`);
export const runQuery = (sparql) => call('/query', { method: 'POST', body: { sparql } });
// Named analysis queries (analyst role, enforced in the backend). Save upserts
// into the Pod's queries.json; delete removes by id.
export const saveQuery = (q) => call('/queries', { method: 'POST', body: q });
export const deleteQuery = (id) => call(`/queries/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const publish = (partner) => call('/partners/publish', { method: 'POST', body: partner });

// The pre-run ingest preview: current watermark, pending row count, record count.
export const getIngestPreview = (sourceId) => call(`/ingest?sourceId=${encodeURIComponent(sourceId)}`);

// Poll a backend job (analysis, ingest) to completion. This is what the held
// credential buys us — the work outlives the request, and a slow federated query
// or bulk ingest can't be killed by a browser navigation. `onProgress` receives
// the job each tick, including its `{done, total, message}` progress.
async function pollJob(id, onProgress) {
  for (;;) {
    await new Promise((r) => setTimeout(r, 500));
    const job = await call(`/jobs/${id}`);
    if (job.status === 'done') return job.result;
    if (job.status === 'failed') throw new Error(job.error);
    onProgress?.(job);
  }
}

// Analysis is a background job on the server: start it, then poll.
export async function runAnalysis({ onProgress } = {}) {
  const { id } = await call('/analysis', { method: 'POST' });
  return pollJob(id, onProgress);
}

// Live ingest: start the job (technical role), then poll — progress drives the
// Run-ingest screen's bar.
export async function runIngest(sourceId, { onProgress } = {}) {
  const { id } = await call('/ingest', { method: 'POST', body: { sourceId } });
  return pollJob(id, onProgress);
}
